import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { KioskQuestionHeading, KioskTimeRow } from '@famly/stat_signin-select-children_lib';
import { Text } from '@famly/mf_data-display_text';
import { Stack } from '@famly/mf_layout_stack';
import { Box, createBox } from '@famly/mf_layout_box';
import { getDisplayHourValue } from 'signin-app/components/list-times/helpers';
import * as GroupsSelectors from 'signin-app/groups/selectors';
import * as ChildSelectors from 'signin-app/child/selectors';
import translate from 'signin-app/helpers/translate';
import TimeBox, {} from 'signin-app/components/time-box';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
const StyledBox = styled(createBox({})) `
    p {
        font-family: ${props => props.theme.fontConfiguration.family};
    }
`;
export const ListTimes = ({ times, selectedHours, selectedMinutes, onHoursChange, onMinutesChange, displayMinutes, e2eHourUnit, isEmployee, title, size = 'regular', reservesMinutesSpace = false, formatMinuteLabel, errorMessage = null, }) => {
    const locale = useTypedSelector(GroupsSelectors.institutionLocale);
    const institutionTitle = useTypedSelector(GroupsSelectors.institutionTitle);
    const minutesWrapperRef = React.useRef(null);
    const { childId: childIdFromParams } = useParams();
    const requirePickupInfo = useTypedSelector(state => ChildSelectors.requirePickupInfo(state, { childId: childIdFromParams }));
    const minutesForSelectedHour = React.useMemo(() => {
        const hiddenPlaceholderMinutes = times.length ? times[0].minutes : [];
        if (selectedHours) {
            const selectedTime = times.find(time => time.hour === selectedHours);
            return selectedTime ? selectedTime.minutes : hiddenPlaceholderMinutes;
        }
        return hiddenPlaceholderMinutes;
    }, [selectedHours, times]);
    const handleHoursClick = React.useCallback(async (hours) => {
        await onHoursChange(hours);
        if (!selectedHours && minutesWrapperRef.current) {
            minutesWrapperRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedHours, onHoursChange]);
    const text = React.useMemo(() => {
        if (title) {
            return title;
        }
        if (isEmployee) {
            return translate('whenWillLeave');
        }
        if (requirePickupInfo) {
            return translate('hour');
        }
        return translate('optionalHour');
    }, [isEmployee, requirePickupInfo, title]);
    if (times.length === 0) {
        return (_jsx(Stack, { flexWrap: "wrap", children: _jsx(Text, { variant: "body", color: "n400", children: translate('emptyOpeningHours', { institutionTitle }) }) }));
    }
    const selectedHourIndex = times.findIndex(time => time.hour === selectedHours);
    const hourBoxes = times.map(time => {
        const displayHourValue = getDisplayHourValue(locale, time.hour);
        return (_jsx(TimeBox, { value: time.hour, displayValue: displayHourValue && displayHourValue.hour, subText: displayHourValue && displayHourValue.amPm, selected: time.hour === selectedHours, handleOnClick: handleHoursClick, dataE2eTimeHour: e2eHourUnit, size: size }, time.hour));
    });
    return (_jsxs(StyledBox, { children: [size === 'compact' ? (_jsx(KioskQuestionHeading, { heading: text, errorMessage: errorMessage })) : (_jsx(Text, { variant: "h6", emphasized: true, color: "n400", children: text })), size === 'compact' ? (_jsx(Box, { mt: 1, children: _jsx(KioskTimeRow, { initialIndex: selectedHourIndex === -1 ? null : selectedHourIndex, children: hourBoxes }) })) : (_jsx(Stack, { mt: 1, flexWrap: "wrap", gap: 3, children: hourBoxes })), selectedHours || reservesMinutesSpace ? (_jsx("div", { ref: minutesWrapperRef, inert: !selectedHours, children: _jsxs(Box, { mt: size === 'compact' ? 4 : 6, opacity: selectedHours ? 1 : 0, children: [_jsx(Text, { variant: "h6", emphasized: true, color: "n400", children: translate('minutes') }), _jsx(Stack, { mt: 1, flexWrap: "wrap", gap: 3, children: minutesForSelectedHour.map(minutes => {
                                const displayMinuteValue = displayMinutes && selectedHours
                                    ? displayMinutes(selectedHours, minutes, locale)
                                    : '';
                                return (_jsx(TimeBox, { value: minutes, selected: minutes === selectedMinutes, handleOnClick: onMinutesChange, displayValue: formatMinuteLabel && selectedHours
                                        ? formatMinuteLabel(selectedHours, minutes)
                                        : displayMinuteValue && displayMinuteValue.minutes, size: size }, minutes));
                            }) })] }) })) : null] }));
};
